// ============================================================================
// L'AIDE — « Aide & tutoriels », un tiroir contextuel (relevé de la maquette, `helpOpen`/`hlpHome`).
//
// ⚠️ CE QUE LA MAQUETTE DESSINE, ET QUE J'AI SUIVI. L'aide n'est pas une rubrique du rail où l'on se
// rend : c'est un tiroir qui s'ouvre PAR-DESSUS l'écran courant. Il s'ouvre sur un ACCUEIL en trois
// blocs — « à propos de cet écran », les procédures écrites, les tours guidés — et la procédure est
// une SECONDE VUE du même tiroir, derrière un retour. Une page d'aide séparée obligerait à quitter ce
// qu'on fait, chercher le bon article, puis revenir : trois gestes pendant lesquels on a oublié la
// question.
//
// ⚠️ LES TROIS BLOCS NE DISENT PAS LA MÊME CHOSE, et c'est pour ça qu'ils sont trois. « À propos de cet
// écran » explique POURQUOI l'écran est ainsi — les décisions de conception, ce qu'on n'affiche pas et
// pourquoi. La PROCÉDURE dit COMMENT faire, étape par étape. Le TOUR montre OÙ, dans l'écran lui-même.
// Les fondre en un seul texte donnerait un article que personne ne lit en entier.
//
// ⚠️ CE QUI N'EST PAS ENCORE LÀ, ET QUI SE DIT PLUTÔT QUE DE SE SIMULER. Les tours guidés (bulle, voile,
// projecteur sur un élément réel, marqueur « Vu ») visent des ANCRES précises dans chaque écran et
// demandent un stockage du « déjà vu ». Ils sont donc LISTÉS — pour qu'on sache ce qui vient — mais
// inertes, et la section le dit. Des boutons qui n'ouvrent rien, c'est §23.0.
// ============================================================================
import { useEffect, useState } from 'react';
import { Icon, type IconName } from '@orgazone/ui-web';
import { key, keyText, t, type Translatable } from '../i18n';

/** Une étape d'une procédure : ce qu'on fait, OÙ, comment — et le piège ou l'astuce s'il y en a un. */
export interface EtapeDeProcedure {
  t: Translatable;
  /** Le chemin dans l'application — c'est ce qui manque le plus souvent à une aide. */
  o: Translatable;
  d: Translatable;
  tip?: Translatable;
  /** ⚠️ Un AVERTISSEMENT n'est pas une astuce : il dit ce qui casse, pas ce qui aide. */
  warn?: Translatable;
}

export interface Procedure {
  /** L'écran auquel elle se rattache — c'est par lui que le tiroir la trouve. */
  ecran: string;
  titre: Translatable;
  /** « 8 étapes · 5 min de lecture » — ce qu'on s'engage à lire avant de cliquer. */
  duree: Translatable;
  intro: Translatable;
  etapes: EtapeDeProcedure[];
}

/** Le texte d'ambiance d'un écran : POURQUOI il est ainsi, pas comment s'en servir. */
export interface ProposDeLEcran {
  /** L'adresse de l'écran ; `?clé=valeur` la restreint à un onglet (`/referentiels?tab=vocab`). */
  ecran: string;
  titre: Translatable;
  /** Chaque entrée est un paragraphe ; le texte gras y est porté par des bornes `**`. */
  paragraphes: Translatable[];
}

/**
 * ⚠️ Textes relevés de la maquette, verbatim QUAND ILS SONT ENCORE VRAIS : ce sont des décisions de
 * produit. Le relevé n'est pas un blanc-seing — une aide qui décrit un écran d'il y a six mois est
 * pire que pas d'aide, parce qu'on la croit sur parole précisément quand on ne comprend plus l'écran.
 */
export const A_PROPOS: ProposDeLEcran[] = [
  {
    ecran: '/referentiels?tab=vocab',
    titre: key('Le vocabulaire'),
    /**
     * ⚠️ ÉCRIT PARCE QUE L'ÉCRAN CHANGE UN MOT, PAS UNE DONNÉE — et que la différence ne se voit pas.
     * Renommer « Saison » en « Millésime » ne touche ni la colonne, ni les listes, ni les articles :
     * seul le mot affiché change. Sans ce paragraphe, on cherche la liste « Millésimes » qu'on croit
     * avoir créée, ou on craint d'avoir renommé quelque chose en base.
     * ⚠️ L'AIDE DIT AUSSI CE QUE L'ÉCRAN NE FAIT PAS : il ne traduit rien, et il ne renomme pas les
     * titres de pages — ceux-là sont une traduction, jamais un choix du magasin.
     */
    paragraphes: [
      key('Chaque champ de l’article a **trois mots possibles** : celui de la plateforme, celui de votre **métier** (posé par l’éditeur — un caviste lit « Millésime » là où le prêt-à-porter lit « Saison »), et **le vôtre**. Le vôtre gagne toujours quand il est écrit ; effacé, le mot du métier revient — jamais du blanc, jamais un code technique.'),
      key('Ce que vous écrivez ici n’est **pas jugé** : « Cuvée », « Collection » ou n’importe quel mot qui parle à votre équipe. Il ne change **que l’étiquette** — ni la colonne, ni les listes, ni les articles, ni le mot des autres magasins. Et il ne vaut que dans **votre langue** : un vendeur qui travaille en espagnol garde le mot espagnol du métier.'),
      key('Cet écran ne **traduit** rien et ne renomme pas les **titres de pages** : ceux-là suivent la langue du compte, et personne ne les modifie — c’est le mot des champs qui vous appartient, pas le nom des écrans.'),
    ],
  },
  {
    ecran: '/marketing',
    titre: key('Les campagnes'),
    /**
     * ⚠️ 🔴 ÉCRIT PARCE QUE LE GROUPE TÉMOIN NE S'EXPLIQUAIT NULLE PART. Il était construit, éprouvé,
     * consigné à la spec — et la première question posée devant l'écran a été « je sais toujours pas
     * à quoi ça sert ». Une mécanique qui demande de NE PAS envoyer des messages ne se devine pas :
     * elle a besoin d'un endroit qui l'explique en entier, et ce tiroir est cet endroit.
     * ⚠️ L'AIDE DIT AUSSI CE QUE L'ÉCRAN NE FAIT PAS. Une aide qui n'énumère que les possibilités
     * laisse croire le reste acquis — et on cherche pendant dix minutes un bouton qui n'existe pas.
     */
    paragraphes: [
      key('Une campagne porte **un seul canal** — SMS ou e-mail — parce que le message, le coût et le consentement ne sont pas les mêmes. Le consentement est relu **au moment de l’envoi**, pas seulement à la construction de la liste : un désabonnement arrivé pendant un envoi de 20 000 messages arrête le suivant.'),
      key('Le **groupe témoin** répond à la seule question qui compte : *cette campagne sert-elle à quelque chose ?* Imaginons 1 000 destinataires et 120 clients revenus ensuite. Ce 120 ne répond pas — une partie serait revenue de toute façon, et vous ne savez pas laquelle. Alors on **n’envoie rien à une centaine d’entre eux**, tirés au hasard, et on compare : si 12 % des servis reviennent contre 8 % du témoin, l’écart de **4 points** est ce que la campagne a causé. Le reste serait arrivé sans elle.'),
      key('Il est **facultatif et désactivé par défaut** : ces clients-là ne recevront pas votre offre, c’est une décision commerciale et elle vous appartient. L’écran vous dit combien de personnes ne recevront rien **avant** que vous validiez. Sur une petite audience il vous prévient que la mesure ne conclura rien — mieux vaut alors s’en passer.'),
      key('Le compte rendu s’**additionne toujours** : partis, en échec, écartés, témoins et reste font le nombre de destinataires. Un écarté n’est jamais effacé de la liste — sinon personne ne pourrait dire où sont passés les manquants.'),
      key('Une **audience enregistrée** garde vos CRITÈRES, jamais la liste des clients du jour : « Gold dormants » désignera les Gold dormants du jour où vous la rejouerez. Réenregistrer sous le même nom REMPLACE — c’est ainsi qu’on corrige une audience sans en accumuler trois.'),
      key('Une campagne part **tout de suite**, à une **date programmée**, ou se **répète** à heure fixe chaque semaine ou chaque mois (étape 5 — « Quand »). Programmée ou répétée, elle part TOUTE SEULE et sa cible est recalculée au moment de chaque envoi ; chaque tour d’une campagne répétée engendre sa propre campagne, avec son propre compte rendu.'),
      key('Ce que cet écran **ne fait pas encore**, et qu’il vaut mieux savoir avant de le chercher : il ne se déclenche pas **client par client** (bienvenue, cagnotte qui expire, client inactif) — une campagne répétée part à tous ceux qui correspondent ce jour-là, pas au moment où l’un d’eux change —, et n’applique **aucun plafond** du nombre de messages par client. Rien n’empêche donc aujourd’hui d’envoyer trois campagnes au même client dans le mois.'),
    ],
  },
  {
    ecran: '/',
    titre: key('Le tableau de bord'),
    /**
     * ⚠️ ADAPTÉ, PAS RECOPIÉ (2026-09-05). La maquette en donnait trois paragraphes ; DEUX étaient
     * devenus faux pour notre écran, et les recopier aurait fait mentir l'aide :
     *  · elle annonçait « qu'est-ce qui bloque, PUIS comment tourne le magasin » et un bandeau qui
     *    n'apparaît que s'il y a à traiter. Notre page fait l'INVERSE — le chiffre d'abord, les
     *    écarts en dernière rangée — et n'a aucun bandeau conditionnel ;
     *  · elle affirmait « le taux de transformation serait un chiffre inventé, nous n'en affichons
     *    pas ». Nous en affichons un depuis la reprise du compteur de passages (171 286 entrées
     *    reprises). C'est la décision inverse, prise depuis.
     * Le paragraphe des comparaisons, lui, est conservé MOT POUR MOT : il est toujours vrai, et
     * c'est une décision de produit qu'il vaut mieux dire avec les mots de celui qui l'a prise.
     */
    paragraphes: [
      key('Il répond à une question : **comment tourne le magasin**. Le chiffre d’affaires et sa courbe d’abord, les indicateurs ensuite, et tout en bas ce qui appelle une action — les écarts à traiter et les campagnes. Chaque carte se masque depuis **Personnaliser** : ce que vous voyez est votre arrangement, pas un écran figé.'),
      key('Toutes les comparaisons se font à **la même période de l’an dernier**, jamais à la période précédente : dans le prêt-à-porter, comparer juillet à juin n’apprend rien.'),
      key('Le **taux de transformation** compare vos tickets aux entrées, et il n’existe que là où un compteur de passages compte vraiment. Sans compteur, la tuile dit « aucun passage compté » et l’anneau reste gris : elle n’affiche jamais un zéro, qui se lirait comme un magasin où personne n’est entré.'),
      key('Ce que cet écran **ne sait pas encore mesurer** est écrit en bas, avec sa raison. Une carte absente s’explique ; une carte remplie de valeurs plausibles ne s’explique pas.'),
    ],
  },
  {
    ecran: '/inventaires',
    titre: key('L’inventaire'),
    paragraphes: [
      key('Le principe tient en une phrase : **le physique constaté fait foi**. Bipé → le compté écrase le théorique. Jamais bipé → remis à zéro. Exclu (cartons fermés, dépôt) → conservé au théorique, aucune écriture.'),
      key('Le magasin **ne ferme pas** : le théorique reste vivant pendant la session, une vente ne crée pas de faux écart, et un article bipé puis vendu s’ajuste tout seul. En revanche **aucune marchandise ne peut entrer** tant que la session tourne : reçue mais pas rangée, elle ferait monter le théorique sans être en rayon, et l’écart ressemblerait à une démarque.'),
      key('Rien ne s’écrit avant la clôture. Ensuite, chaque différence devient un **mouvement signé**, visible dans Mouvements et scellé dans le rapport — imprimable, exportable, envoyable à la compta.'),
    ],
  },
];

/**
 * LES PROCÉDURES ÉCRITES — texte relevé de la maquette (`PROCS`), complété des règles décidées depuis.
 *
 * ⚠️ ELLES DISENT LES INTERDITS AUTANT QUE LES GESTES. Une aide qui n'explique que le chemin heureux
 * laisse découvrir les refus devant un camion déchargé.
 */
export const PROCEDURES: Procedure[] = [
  {
    ecran: '/marketing',
    titre: key('Lancer une campagne, de A à Z'),
    duree: key('7 étapes · 4 min de lecture'),
    intro: key('Le principe : une campagne se FIGE avant de partir. Figer, c’est transformer des critères en une liste de destinataires nommés, dans un ordre stable — c’est ce qui rend l’envoi reprenable et le compte rendu vérifiable.'),
    etapes: [
      {
        t: key('Choisir le canal'),
        o: key('Marketing → Nouvelle campagne → étape 1'),
        d: key('SMS ou e-mail. Le choix est définitif pour cette campagne : le message, le coût et le consentement diffèrent, et une même liste n’est pas joignable des deux façons.'),
        warn: key('Le SMS n’accepte que les numéros MOBILES. Compter les fixes « pour ne perdre personne » ferait partir des messages facturés vers des lignes qui n’en reçoivent pas.'),
      },
      {
        t: key('Écrire le message'),
        o: key('Étapes 2 et 3'),
        d: key('Les champs {prénom}, {points}, {palier} et {bon} sont remplacés à l’envoi. Le compteur de SMS est une ESTIMATION : un caractère accentué hors table GSM peut doubler le nombre de messages facturés.'),
        tip: key('Une fois l’audience figée, le message ne se modifie plus — deux textes sous un seul compte rendu seraient impossibles à démêler.'),
      },
      {
        t: key('Choisir qui reçoit'),
        o: key('Étape 4 — « À qui »'),
        d: key('Palier de fidélité, activité (venus sur une période, ou pas venus depuis), achats minimum, cagnotte minimum, et mois d’anniversaire — ce dernier accepte PLUSIEURS mois, pour couvrir un trimestre sans faire trois campagnes. Le compte se met à jour à chaque modification.'),
        warn: key('Les fiches sans date de naissance sont écartées dès qu’un mois est choisi : on ne souhaite pas un anniversaire à quelqu’un dont on ignore la date.'),
        tip: key('Nommez l’audience en bas de l’étape pour la retrouver la prochaine fois : ce sont les CRITÈRES qui sont gardés, donc elle se recalcule à chaque réutilisation.'),
      },
      {
        t: key('Décider du groupe témoin'),
        o: key('Étape 4 — encadré vert, à droite'),
        d: key('Réserver 5, 10 ou 20 % de l’audience qui ne recevra RIEN. C’est le seul moyen de savoir ensuite ce que la campagne a causé, plutôt que ce qui serait arrivé de toute façon. L’écran affiche combien de personnes ne recevront rien avant que vous validiez.'),
        tip: key('Sur une petite audience, laissez « Aucun » : un témoin de quelques dizaines de personnes ne permettra de conclure à rien, et vous aurez renoncé aux envois pour rien.'),
      },
      {
        t: key('Choisir quand'),
        o: key('Étape 5 — « Quand »'),
        d: key('Maintenant, à une date et une heure, ou à heure fixe chaque semaine ou chaque mois. Programmée ou répétée, la campagne part TOUTE SEULE : sa cible est recalculée au moment de l’envoi, et chaque tour engendre une campagne traçable.'),
      },
      {
        t: key('Vérifier le solde de crédits, puis lancer'),
        o: key('Détail de la campagne → « Lancer l’envoi »'),
        d: key('Le solde est relu à l’ouverture du panneau. L’effectif comparé est CE QUI RESTE à envoyer — les déjà-partis sont payés, et les témoins ne consomment aucun crédit.'),
        warn: key('Un solde insuffisant ne BLOQUE pas l’envoi : les premiers messages partent, les derniers échouent chez l’opérateur. Rechargez avant, ou resserrez la cible.'),
      },
      {
        t: key('Lire le compte rendu, puis l’effet'),
        o: key('Détail de la campagne'),
        d: key('Partis, en échec, écartés, témoins et reste s’additionnent au nombre de destinataires. Quelques jours plus tard, la tuile Campagnes du tableau de bord montre ce que les destinataires ont acheté — et, si vous aviez un témoin, l’écart que la campagne a causé.'),
        tip: key('Un envoi interrompu se REPREND au destinataire suivant : personne n’est servi deux fois, et personne n’est sauté.'),
      },
    ],
  },
  {
    ecran: '/inventaires',
    titre: key('Mener un inventaire, de A à Z'),
    duree: key('8 étapes · 5 min de lecture'),
    intro: key('Le principe qui guide tout : on compte ce qu’on voit, jamais l’inverse. À la clôture le scan fait foi — et le magasin reste ouvert du début à la fin.'),
    etapes: [
      {
        t: key('Lancer la session'),
        o: key('Stock → Inventaires → Nouvel inventaire'),
        d: key('Choisissez le type (complet, tournant, ciblé, marque, secteur), la date de clôture visée, puis les zones — pré-créées ou ajoutées au fil des scans.'),
        tip: key('Un inventaire tournant par famille, régulier, évite le marathon de fin d’exercice.'),
      },
      {
        t: key('Ce qu’un inventaire complet fige'),
        o: key('Au démarrage'),
        d: key('Un complet fige les références VIVANTES : tout ce qui a du stock, positif ou négatif, et tout ce qui a bougé depuis la clôture du dernier inventaire complet. Ce qui dort à zéro depuis des années n’est pas figé — il n’y a rien à y compter, et le lister noierait les références qui comptent.'),
        tip: key('Si vous trouvez en rayon un article que la photo n’a pas pris, bipez-le quand même : il entre dans l’inventaire à zéro et devient un excédent.'),
      },
      {
        t: key('Aucune entrée de marchandise pendant la session'),
        o: key('Réceptions, entrées manuelles, rétrocessions'),
        d: key('Tant qu’un inventaire est en cours, la marchandise qui arrive ne peut pas être intégrée : réception d’un bon, entrée manuelle et réception d’une rétrocession sont refusées.'),
        warn: key('Une marchandise reçue pendant le comptage fait monter le stock théorique sans être en rayon : l’écart qui en résulte ressemble en tout point à une démarque, et plus personne ne peut faire la différence. Les CORRECTIONS restent possibles (annulation, régularisation d’un écart, ajustement de niveau) : ce ne sont pas des pièces qui arrivent.'),
      },
      {
        t: key('Compter zone par zone'),
        o: key('Barre de scan → sélecteur Zone'),
        d: key('Choisissez la zone active dans le sélecteur (recherche + création à la volée), puis bipez à la douchette. Chaque bip s’impute à la zone active et fait vivre sa carte.'),
        warn: key('Sans zone active, le bip est refusé — chaque pièce comptée doit dire où elle était.'),
      },
      {
        t: key('Laisser venir les Mobile Partner'),
        o: key('Cartes de zone'),
        d: key('Les terminaux comptent hors ligne puis poussent leurs comptages tout seuls : la zone se crée automatiquement avec le nom saisi sur l’appareil, et son arrivée s’annonce à l’écran. Pendant qu’un appareil compte, sa zone est verrouillée : on n’y bipe pas à la douchette, sinon les deux comptages se cumuleraient.'),
      },
      {
        t: key('Corriger les écarts, pièce en main'),
        o: key('Onglet Écarts'),
        d: key('Le tableau compare compté et théorique, trié par ampleur d’écart. Recomptez, ajustez au + / − ou en saisie directe : rien ne s’écrit avant la clôture, vous corrigez librement.'),
        tip: key('Les boutons + et − ajoutent ou retirent une pièce dans la zone active ; la saisie, elle, REMPLACE ce que porte cette zone. Quand un article est compté ailleurs aussi, le total toutes zones est rappelé sous le champ.'),
      },
      {
        t: key('Trancher les non scannés'),
        o: key('Onglet Non scannés'),
        d: key('Tout ce qui n’a jamais été bipé sera remis à zéro à la clôture, automatiquement. Votre seul travail ici : exclure l’impossible à scanner (cartons fermés au dépôt…) — un par un ou en masse via les filtres marque / famille / saison.'),
        warn: key('Un article exclu garde son théorique tel quel — n’excluez que ce que vous ne pouvez physiquement pas compter. Et la case en tête de colonne ne coche que les lignes affichées : au-delà, utilisez « Exclure les N du filtre ».'),
      },
      {
        t: key('Clôturer et archiver'),
        o: key('Bouton Clôturer → assistant en 3 étapes'),
        d: key('L’assistant relit tout : ce qui a été constaté, ce qui va être effacé, puis la confirmation. À la validation, chaque différence devient un mouvement de stock, le stock est réaligné et le rapport est scellé — imprimable, exportable, envoyable par mail, et réimprimable à l’identique des années plus tard.'),
      },
    ],
  },
];

/** Les tours de la maquette, avec leur poids. ⚠️ Listés pour qu'on sache ce qui vient, jamais cliquables. */
const TOURS: { icone: IconName; titre: Translatable; etapes: Translatable }[] = [
  { icone: 'grid', titre: key('Prise en main'), etapes: key('5 étapes') },
  { icone: 'chart', titre: key('Lire son tableau de bord'), etapes: key('8 étapes') },
  { icone: 'box', titre: key('Maîtriser la liste des articles'), etapes: key('7 étapes') },
  { icone: 'percent', titre: key('Changer les prix, du plus fin au plus large'), etapes: key('5 étapes') },
  { icone: 'pulse', titre: key('Surveiller ses caisses'), etapes: key('5 étapes') },
  { icone: 'barcode', titre: key('Mener un inventaire'), etapes: key('5 étapes') },
  { icone: 'inbound', titre: key('Réceptionner la marchandise'), etapes: key('9 étapes') },
];

/** Le ton d'un encadré d'étape — une astuce et un avertissement ne se lisent pas pareil. */
const ENCADRES = {
  tip: { bg: 'var(--teal-100)', fg: 'var(--teal-800)', mot: key('Astuce') },
  warn: { bg: 'var(--amber-bg)', fg: 'var(--amber-fg)', mot: key('Attention') },
} as const;

/** Le surtitre d'une section — 10,5 px, capitales, relevé de la maquette. */
/**
 * Le surtitre d'une section du tiroir — DEUX rôles, relevés (2026-09-05).
 *
 * ⚠️ LE TEAL EST RÉSERVÉ À « À PROPOS DE CET ÉCRAN », et ce n'est pas une décoration : c'est la seule
 * section qui dit POURQUOI l'écran existe, et elle porte la couleur de la marque. « Procédures » et
 * « Tours guidés » listent des choses à faire — elles restent en encre neutre. Peindre les trois en
 * teal ferait de la couleur un ornement ; les peindre toutes en gris perdrait la distinction.
 *
 * ⚠️ POLICE À CHASSE FIXE, qui manquait entièrement : c'est elle qui fait lire ces lignes comme des
 * ÉTIQUETTES et non comme des titres, et c'est la convention de tous les surtitres de la plateforme.
 *
 * ⚠️ AUCUNE MARGE HAUTE — l'espacement entre sections vient du BLOC QUI PRÉCÈDE, jamais du titre qui
 * suit. Avec une marge haute de 20 px, la première section descendait de 38 px du haut du tiroir au
 * lieu de 18 : le rembourrage du corps et la marge du titre s'ajoutaient.
 */
function Surtitre({ children, marque }: { children: string; marque?: boolean }) {
  return (
    <div style={{
      fontFamily: 'var(--font-mono)', fontSize: '10.5px', fontWeight: 600,
      letterSpacing: '.09em', textTransform: 'uppercase',
      color: marque ? 'var(--teal-700)' : 'var(--ink-500)',
      margin: `0 0 ${marque ? 8 : 10}px`,
    }}>
      {children}
    </div>
  );
}

/** La pastille d'icône d'une ligne — même dessin pour une procédure et pour un tour. */
/**
 * La pastille d'icône du tiroir — DEUX tailles, relevées (2026-09-05).
 * ⚠️ 38 EN TÊTE, 36 DANS LA LISTE : ce n'est pas une inattention du dessin, c'est une hiérarchie. La
 * pastille de l'en-tête nomme le tiroir entier, celles de la liste nomment une ligne. Les égaliser
 * mettait le titre au même rang que ses items.
 * ⚠️ `--r-ctrl` ET NON `--r-sm` : 10 contre 11 — deux pixels de rayon, mais c'est le rayon des
 * CONTRÔLES, et la pastille est ronde comme les boutons de la barre, pas comme une carte.
 */
function Pastille({ nom, atone, tete }: { nom: IconName; atone?: boolean; tete?: boolean }) {
  const cote = tete ? 38 : 36;
  return (
    <span style={{
      flexShrink: 0, width: cote, height: cote, borderRadius: 'var(--r-ctrl)',
      background: atone ? 'var(--ink-100)' : 'var(--teal-100)',
      color: atone ? 'var(--ink-400)' : 'var(--teal-700)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <Icon name={nom} size={tete ? 19 : 18} stroke={1.75} />
    </span>
  );
}

/** Un paragraphe dont les bornes `**` deviennent du gras — le texte reste lisible en données. */
/**
 * ⚠️ LE TEXTE ARRIVE EN CLÉ, il se RÉSOUT ICI — et AVANT le découpage sur `**` : découper d'abord
 * passerait au résolveur des demi-phrases qui ne sont des clés d'aucun catalogue.
 */
function Paragraphe({ texte }: { texte: Translatable }) {
  return (
    <p style={{ margin: '0 0 10px', fontSize: '13.5px', color: 'var(--ink-600)', lineHeight: 1.62 }}>
      {t(texte).split('**').map((bout, i) => (i % 2 === 1
        ? <strong key={bout} style={{ color: 'var(--ink-900)' }}>{bout}</strong>
        : <span key={bout}>{bout}</span>))}
    </p>
  );
}

/**
 * Cet écran est-il celui de l'aide demandée ?
 *
 * ⚠️ LA RACINE NE PEUT PAS SE COMPARER PAR PRÉFIXE (corrigé 2026-09-05, avant d'y ajouter un texte).
 * `pathname.startsWith('/')` est vrai de TOUTES les adresses : l'à-propos du tableau de bord se
 * serait affiché sur chaque écran du back-office, en annonçant partout « voici pourquoi cet écran
 * existe ». Le défaut n'apparaît qu'au moment où l'on écrit la première entrée pour `/` — donc
 * jamais pendant qu'on écrit les autres.
 */
export function concerne(pathname: string, ecran: string, search = ''): boolean {
  const [path = '', query] = ecran.split('?');
  const onPath = path === '/' ? pathname === '/' : pathname.startsWith(path);
  if (!onPath || query === undefined) return onPath;
  // ⚠️ UN ONGLET SE NOMME DANS L'ADRESSE : l'aide d'un onglet ne s'affiche que sur LUI — sur les autres onglets du
  //    même écran, elle parlerait d'une chose que l'utilisateur n'a pas sous les yeux.
  const have = new URLSearchParams(search);
  return [...new URLSearchParams(query)].every(([k, v]) => have.get(k) === v);
}

/**
 * LE TIROIR « AIDE & TUTORIELS » — accueil, puis procédure.
 *
 * ⚠️ IL SE FERME À ÉCHAP ET AU CLIC SUR LE VOILE : une aide qu'on n'arrive pas à refermer interrompt
 * ce qu'on était en train de faire, ce qui est exactement l'inverse de son rôle.
 * ⚠️ `procDirecte` OUVRE DIRECTEMENT LA PROCÉDURE — c'est ce que demande le lien du wizard, qui parle
 * déjà de l'inventaire : le faire atterrir sur l'accueil ajouterait un clic pour rien.
 */
export function PanneauAide({ pathname, search = '', procDirecte, onClose }: {
  pathname: string; search?: string; procDirecte?: boolean; onClose: () => void;
}) {
  const propos = A_PROPOS.find((p) => concerne(pathname, p.ecran, search)) ?? null;
  const proc = PROCEDURES.find((p) => concerne(pathname, p.ecran, search)) ?? null;
  const [ouverte, setOuverte] = useState(procDirecte === true && proc != null);

  useEffect(() => {
    const auClavier = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', auClavier);
    return () => window.removeEventListener('keydown', auClavier);
  }, [onClose]);

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 600, display: 'flex', justifyContent: 'flex-end',
      background: 'rgba(12,14,16,.35)',
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        // ⚠️ MÉTRIQUES RELEVÉES, pas choisies : 404 px, 92vw au plus, ombre -14/44/-14, glissement de
        //    0,2 s depuis la droite. Une largeur « qui va bien » finit toujours par diverger.
        // ⚠️ LE CORPS DÉFILE, PAS LE TIROIR (aligné 2026-09-05). Le tiroir défilait en entier avec un
        //    en-tête `sticky` : même intention, autre mécanique — et un `sticky` laisse passer un
        //    liseré de contenu sous sa bordure au premier pixel de défilement. La maquette pose une
        //    colonne flex dont seul le corps déborde ; l'en-tête ne bouge alors pas du tout.
        width: 404, maxWidth: '92vw', height: '100%',
        display: 'flex', flexDirection: 'column',
        background: 'var(--paper)', boxShadow: '-14px 0 44px -14px rgba(12,14,16,.3)',
        animation: 'oz-slide-r .2s cubic-bezier(.2,.7,.3,1)',
      }}>
        {/* L'en-tête : pastille, titre, et ce que le tiroir contient — relevé de la maquette. */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '18px 20px', flexShrink: 0,
          borderBottom: '1px solid var(--ink-200)',
        }}>
          <Pastille nom="help" tete />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '16.5px', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--ink-900)' }}>
              
              {t('Aide & tutoriels')}
            </div>
            <div style={{ fontSize: '12.5px', color: 'var(--ink-500)' }}>{t('Article de l’écran + tours guidés')}</div>
          </div>
          <button onClick={onClose} aria-label={t('Fermer')}
            style={{ width: 34, height: 34, border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--ink-100)', color: 'var(--ink-600)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
            <Icon name="x" size={16} stroke={2.2} />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 20px' }}>
          {ouverte && proc ? (
            <>
              <button onClick={() => setOuverte(false)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, margin: '14px 0 12px', padding: 0, border: 'none', background: 'none', color: 'var(--ink-500)', fontSize: 'var(--t-dense)', fontWeight: 600, cursor: 'pointer' }}>
                <Icon name="chevronL" size={13} stroke={2.2} />{t('Toutes les aides')}
              </button>
              <h3 style={{ margin: '0 0 6px', fontSize: '15.5px', fontWeight: 700, color: 'var(--ink-900)' }}>{t(proc.titre)}</h3>
              <p style={{ margin: '0 0 18px', fontSize: '13.5px', color: 'var(--ink-600)', lineHeight: 1.65 }}>{t(proc.intro)}</p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {proc.etapes.map((e, i) => (
                  <div key={keyText(e.t)} style={{ display: 'flex', gap: 11, alignItems: 'flex-start' }}>
                    {/* ⚠️ LE NUMÉRO EST UNE SÉQUENCE RÉELLE : ces étapes s'enchaînent dans le temps, on
                        ne peut pas clôturer avant d'avoir compté. Numéroter une liste qui n'est pas
                        ordonnée serait une décoration qui ment. */}
                    <span style={{ flexShrink: 0, width: 22, height: 22, borderRadius: 'var(--r-pill)', background: 'var(--teal-100)', color: 'var(--teal-800)', fontSize: 'var(--t-meta)', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {i + 1}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--ink-900)' }}>{t(e.t)}</div>
                      {/* Le CHEMIN dans l'application — c'est ce qui manque le plus souvent à une aide. */}
                      <div style={{ fontSize: '11px', color: 'var(--ink-400)', fontFamily: 'var(--font-mono)', margin: '2px 0 5px' }}>{t(e.o)}</div>
                      <div style={{ fontSize: '12.5px', color: 'var(--ink-600)', lineHeight: 1.65 }}>{t(e.d)}</div>
                      {(['warn', 'tip'] as const).map((k) => (e[k] ? (
                        <div key={k} style={{ marginTop: 7, padding: '8px 11px', borderRadius: 'var(--r-sm)', background: ENCADRES[k].bg, color: ENCADRES[k].fg, fontSize: '12px', lineHeight: 1.6 }}>
                          <strong>{t(ENCADRES[k].mot)} — </strong>{t(e[k]!)}
                        </div>
                      ) : null))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              {/* ── À PROPOS DE CET ÉCRAN — le POURQUOI, pas le comment ─────────────────────────── */}
              {/* ⚠️ L'ESPACEMENT VIT SUR LE BLOC, PAS SUR LE TITRE QUI SUIT (relevé : `margin-bottom:
                  22px`). Les deux donnent le même écart, mais un seul survit à la disparition d'une
                  section : porté par le titre suivant, il s'en irait avec lui et le bloc du dessus
                  viendrait coller au bord. */}
              <div style={{ marginBottom: 22 }}>
              <Surtitre marque>{t('À propos de cet écran')}</Surtitre>
              {propos ? (
                <>
                  <h3 style={{ margin: '0 0 8px', fontSize: '15.5px', fontWeight: 700, letterSpacing: '-0.015em', color: 'var(--ink-900)' }}>{t(propos.titre)}</h3>
                  {propos.paragraphes.map((texte) => <Paragraphe key={keyText(texte).slice(0, 24)} texte={texte} />)}
                </>
              ) : (
                // ⚠️ « RIEN POUR CET ÉCRAN » N'EST PAS UN ÉCHEC, et le dire vaut mieux qu'un tiroir
                //    vide : un vide se lit comme un chargement raté.
                <p style={{ margin: 0, fontSize: '13.5px', color: 'var(--ink-500)', lineHeight: 1.65 }}>
                  
                  {t('Cet écran n’a pas encore son article. Ils arrivent écran par écran.')}
                </p>
              )}
              </div>

              {/* ── PROCÉDURES PAS À PAS ────────────────────────────────────────────────────────── */}
              <div style={{ marginBottom: 22 }}>
              <Surtitre>{t('Procédures pas à pas')}</Surtitre>
              {proc ? (
                <button onClick={() => setOuverte(true)}
                  style={{ display: 'flex', alignItems: 'center', gap: 11, width: '100%', padding: '10px 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', background: 'var(--paper)', cursor: 'pointer', textAlign: 'left' }}>
                  <Pastille nom="file" />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: '13.5px', fontWeight: 600, color: 'var(--ink-900)' }}>{t(proc.titre)}</span>
                    <span style={{ display: 'block', fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>{t(proc.duree)}</span>
                  </span>
                  <Icon name="chevronR" size={15} stroke={2} />
                </button>
              ) : null}
              <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginTop: 8, padding: '10px 12px', border: '1px dashed var(--ink-300)', borderRadius: 'var(--r-md)', background: 'var(--ink-50)' }}>
                <Pastille nom="plus" atone />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: '13.5px', fontWeight: 600, color: 'var(--ink-700)' }}>{t('D’autres procédures arrivent')}</span>
                  <span style={{ display: 'block', fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>{t('Contrôler un BL, régler un écart de caisse, créer une promo…')}</span>
                </span>
              </div>
              </div>

              {/* ── TOURS GUIDÉS ───────────────────────────────────────────────────────────────── */}
              <Surtitre>{t('Tours guidés')}</Surtitre>
              {/* ⚠️ LISTÉS MAIS INERTES, ET LA SECTION LE DIT. Un tour désigne des éléments PRÉCIS dans
                  l'écran : il lui faut une ancre par étape, sur chaque écran, et un « déjà vu » à
                  retenir. Sept boutons qui n'ouvrent rien seraient pires que leur absence (§23.0) ;
                  les LISTER dit ce qui vient, ce qu'un simple « à venir » ne dirait pas. */}
              <p style={{ margin: '0 0 9px', fontSize: 'var(--t-dense)', color: 'var(--ink-500)', lineHeight: 1.6 }}>
                
                {t('Les tours montrent le parcours dans l’écran lui-même, avec la bulle qui désigne le bouton dont elle parle. Ils arrivent avec leurs repères ; d’ici là, la liste dit ce qui est prévu.')}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {TOURS.map((tour) => (
                  <div key={keyText(tour.titre)} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '9px 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', background: 'var(--ink-50)', opacity: 0.72 }}>
                    <Pastille nom={tour.icone} atone />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: '13.5px', fontWeight: 600, color: 'var(--ink-700)' }}>{t(tour.titre)}</span>
                      <span style={{ display: 'block', fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>{t(tour.etapes)}</span>
                    </span>
                    <span style={{ fontSize: '10.5px', fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--ink-400)', whiteSpace: 'nowrap' }}>{t('À venir')}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
